import { TFunction } from "i18next";
import * as yup from "yup";

import ParentshipTypeEnum from "./ParentshipTypeEnum";

interface Props {
  t: TFunction;
}

export const OtherParentBasicInfoValidation = (t: TFunction) => {
  const requiredMsg = t("validations.required");
  const oibFormat = t("validations.oibFormat");
  const oibFormatValidation = t("validations.oibValidation");

  return {
    firstName: yup.string().trim().required(requiredMsg),
    lastName: yup.string().trim().required(requiredMsg),
    isOibNotExistent: yup.boolean(),
    oib: yup.string().when("isOibNotExistent", {
      is: false,
      then: yup
        .string()
        .required(requiredMsg)
        .matches(/^[0-9]+$/, oibFormat)
        .min(11, oibFormat)
        .max(11, oibFormat)
        .test("checkOibValidation", "isRequired", function (value) {
          var length = value?.length;
          var oibValue = value ?? "";

          if (length) {
            var residue = 10;
            for (var i = 0; i < length - 1; i++) {
              var digit = parseInt(oibValue[i]);
              var midResult = (digit + residue) % 10;
              midResult = midResult === 0 ? 10 : midResult;
              residue = (midResult * 2) % 11;
            }

            var lastDigit = parseInt(oibValue[length - 1]);
            var controllCharacter = 11 - residue;
            if (controllCharacter === lastDigit) {
              return true;
            } else if (controllCharacter === 10 && 0 === lastDigit) {
              return true;
            } else {
              return this.createError({
                path: "oib",
                message: oibFormatValidation,
              });
            }
          } else {
            return this.createError({
              path: "oib",
              message: oibFormatValidation,
            });
          }
        }),
      otherwise: yup.string().nullable(),
    }),
    documentNumber: yup.string().when("isOibNotExistent", {
      is: true,
      then: yup.string().trim().required(requiredMsg),
      otherwise: yup.string().nullable(),
    }),
    documentTypeId: yup.mixed().when("isOibNotExistent", {
      is: true,
      then: yup.mixed().required(requiredMsg),
      otherwise: yup.mixed().nullable(),
    }),
    reasonNonExistenceOibId: yup.mixed().when("isOibNotExistent", {
      is: true,
      then: yup.mixed().required(requiredMsg),
      otherwise: yup.mixed().nullable(),
    }),
    yearOfBirth: yup.number().when("isOibNotExistent", {
      is: true,
      then: yup.number().required(requiredMsg),
      otherwise: yup.number().nullable(),
    }),
    sexId: yup.mixed().when("isOibNotExistent", {
      is: true,
      then: yup.mixed().required(requiredMsg),
      otherwise: yup.mixed().nullable(),
    }),
    professionalQualificationId: yup.mixed().when("isOibNotExistent", {
      is: true,
      then: yup.mixed().required(requiredMsg),
      otherwise: yup.mixed().nullable(),
    }),
  };
};

const OtherParentValidationSchema = ({ t }: Props) => {
  const requiredMsg = t("validations.required");
  const maxNumberOfCharacters = `${t("validations.maxNumberOfCharacters", {
    numberOfCharacters: 60,
  })}`;

  return yup.object().shape({
    ...OtherParentBasicInfoValidation(t),
    email: yup.string().email(t("validations.email")).nullable(),
    professionName: yup.string().when("isOibNotExistent", {
      is: true,
      then: yup
        .string()
        .trim()
        .required(requiredMsg)
        .max(60, maxNumberOfCharacters),
      otherwise: yup.string().max(60, maxNumberOfCharacters).nullable(),
    }),
    parentshipTypeId: yup.mixed().required(requiredMsg),
    custody: yup.string().when("parentshipTypeId", {
      is: (parentshipTypeId: ParentshipTypeEnum) =>
        parentshipTypeId === ParentshipTypeEnum.Custodian,
      then: yup.string().required(requiredMsg).nullable(),
      otherwise: yup.string().nullable(),
    }),
  });
};

export default OtherParentValidationSchema;
